import { payersApi } from "@api";
import { queriesPayers } from "@query";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_IDENTITY_VERIFICATION_REQUEST } from "../../../../query/identityVerification/keys";
import { getIdentityVerificationRequest } from "../../../../services/identityVerification/getIdentityVerificationRequest";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";

import { getIdentityVerificationRequestFromCache } from "./configs/helpers/getIdentityVerificationRequestFromCache";
import { usePayeeIdentityVerificationConfig } from "./configs/payee/usePayeeIdentityVerificationConfig";
import { usePayerIdentityVerificationConfig } from "./configs/payer/usePayerIdentityVerificationConfig";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const userId = useUserId();

  const identityVerificationsRequestQueries = getDataSourceQueries(
    QUERY_IDENTITY_VERIFICATION_REQUEST,
    requirements,
    getIdentityVerificationRequest,
    RequirementType.IdentityVerification
  );

  const payersQueries = requirements
    .filter(
      requirement =>
        requirement.requirementType === RequirementType.IdentityVerification &&
        requirement.payerId &&
        userId !== requirement.payerId
    )
    .map(requirement => ({
      queryKey: queriesPayers.getPayer.getQueryKey({
        payerId: requirement.payerId ?? ""
      }),
      queryFn: () => payersApi.getPayer({ payerId: requirement.payerId ?? "" }),
      ...sharedRequirementsQueriesConfig
    }));

  const queries = [...identityVerificationsRequestQueries, ...payersQueries];

  return useWSQueries({ queries });
};

export const useIdentityVerificationRequirementConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.IdentityVerification] => {
  const contextQueries = useContextQueries(requirements);
  const payerConfig = usePayerIdentityVerificationConfig(callback);
  const payeeConfig = usePayeeIdentityVerificationConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<
      IIdentityVerificationRequestResponse,
      RequirementType.IdentityVerification
    >({
      payerConfig,
      payeeConfig,
      getAdditionalData: getIdentityVerificationRequestFromCache
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getIdentityVerificationRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
